import { cn } from '@/lib/utils';
import {
  Users, AlertTriangle, CheckCircle2, Scale, Loader2,
} from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { MediatorConsensus } from '@/hooks/useStrategicStream';

export function MediatorShowcase({ consensus, isActive, isSmallScreen }: {
  consensus?: MediatorConsensus; isActive: boolean; isSmallScreen: boolean;
}) {
  return (
    <div className="space-y-4">
      {consensus && consensus.keyAgreements && consensus.keyAgreements.length > 0 && (
        <div className={cn(
          'relative p-4 rounded-xl backdrop-blur-sm border transition-all duration-300',
          'bg-gradient-to-r from-cyan-500/15 to-cyan-600/5 border-cyan-500/40',
          isActive && 'ring-2 ring-cyan-400/50 shadow-lg shadow-cyan-500/20'
        )}>
          <div className="flex items-center gap-2 text-sm text-cyan-400 font-mono mb-2">
            <Users className="w-4 h-4" />
            <span className="font-bold uppercase tracking-wider">Key Agreements</span>
            {isActive && (
              <span className="ml-2 w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            )}
          </div>
          <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
            <ul className="space-y-2">
              {consensus.keyAgreements.map((agreement, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>{agreement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {consensus && consensus.unresolvedTensions && consensus.unresolvedTensions.length > 0 && (
        <div className={cn(
          'relative p-4 rounded-xl backdrop-blur-sm border transition-all duration-300',
          'bg-gradient-to-r from-amber-500/15 to-amber-600/5 border-amber-500/40'
        )}>
          <div className="flex items-center gap-2 text-sm text-amber-400 font-mono mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-bold uppercase tracking-wider">Unresolved Tensions</span>
          </div>
          <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
            <ul className="space-y-2">
              {consensus.unresolvedTensions.map((tension, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{tension}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {consensus && consensus.finalRecommendation && !isActive && (
        <div className="relative p-4 rounded-xl backdrop-blur-sm bg-gradient-to-r from-verdict-primary/20 to-amber-500/10 border border-verdict-primary/40">
          <div className="absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-verdict-primary to-transparent" />
          <div className="flex items-center gap-2 text-sm text-verdict-primary font-mono mb-3 pt-1">
            <Scale className="w-5 h-5" />
            <span className="font-bold uppercase tracking-wider">Final Consensus</span>
            <span className="ml-auto px-2 py-0.5 rounded-full bg-verdict-primary/20 text-verdict-primary font-bold text-sm">
              {(consensus.confidence * 100).toFixed(0)}%
            </span>
          </div>
          <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
            <MarkdownContent content={consensus.finalRecommendation} className={isSmallScreen ? 'text-sm' : 'text-base'} />
          </div>
          {consensus.rationale && (
            <div className="mt-3 pt-3 border-t border-verdict-primary/20">
              <div className="text-xs text-verdict-primary/70 uppercase tracking-wide mb-2 font-medium">Rationale</div>
              <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <MarkdownContent content={consensus.rationale} className={isSmallScreen ? 'text-xs' : 'text-sm'} />
              </div>
            </div>
          )}
        </div>
      )}

      {isActive && !consensus && (
        <div className="text-center text-text-muted py-4 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Mediator building consensus...
        </div>
      )}
    </div>
  );
}
